import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  setCustomerDefaultPersistence,
  deleteCustomerDefaultPersistence,
  deletePersistence,
} from '@api/persistence';
import { message } from 'antd';

/**
 * Hook for admin operations on customer default metrics
 */
export const useCustomerLevelDefaultsConfig = () => {
  const queryClient = useQueryClient();

  const setCustomerDefaultMutation = useMutation(
    (config: any) => setCustomerDefaultPersistence(config),
    {
      onSuccess: (_data, variables: any) => {
        message.success('Default Configuration saved successfully');
        // Use key from variables to invalidate the correct query
        const currentKey = variables?.key;
        if (currentKey) {
          queryClient.invalidateQueries(['persistence', currentKey]);
        }
      },
      onError: (error: any) => {
        message.error('Failed to save Default Configuration');
        // eslint-disable-next-line no-console
        console.error('Error setting overview default:', error);
      },
    },
  );

  const deleteCustomerDefaultMutation = useMutation(
    (data: any) => deleteCustomerDefaultPersistence(data),
    {
      onSuccess: (_data, variables: any) => {
        message.success('Default Configuration deleted successfully');
        // Use key from variables to invalidate the correct query
        const currentKey = variables?.key;
        if (currentKey) {
          queryClient.invalidateQueries(['persistence', currentKey]);
        }
      },
      onError: (error: any) => {
        message.error('Failed to delete Default Configuration');
        // eslint-disable-next-line no-console
        console.error('Error deleting Default Configuration:', error);
      },
    },
  );

  const applyToAll = useCallback(
    (configuration: any, key: string) => {
      if (!key) {
        message.error('No key provided for persistence');
        return;
      }

      if (!configuration || configuration === null || configuration === undefined) {
        message.error('Configuration cannot be null or undefined');
        return;
      }

      if (typeof configuration === 'object' && Object.keys(configuration).length === 0) {
        message.error('Configuration is empty and cannot be applied');
        return;
      }

      setCustomerDefaultMutation.mutate({
        configuration,
        key,
      });
    },
    [setCustomerDefaultMutation],
  );

  const removeCustomerDefault = useCallback(
    (key: string) => {
      if (!key) {
        message.error('No key provided for persistence');
        return;
      }

      deleteCustomerDefaultMutation.mutate({ key });
    },
    [deleteCustomerDefaultMutation],
  );

  return {
    applyToAll,
    removeCustomerDefault,
    isSettingDefault: setCustomerDefaultMutation.isLoading,
    isDeletingDefault: deleteCustomerDefaultMutation.isLoading,
  };
};

/**
 * Hook for resetting user configuration to default
 * Deletes the user's personal persistence, falling back to customer default or system default
 */
export const useResetToDefault = () => {
  const queryClient = useQueryClient();

  const resetMutation = useMutation(
    async (key: string) => {
      if (!key) {
        throw new Error('No key provided for persistence');
      }

      // Delete user's personal persistence for overview dashboard
      return deletePersistence(key);
    },
    {
      onSuccess: (_data, key: string) => {
        message.success('Configuration reset to default');
        if (key) {
          queryClient.invalidateQueries(['persistence', key]);
        }
        // Reload the page to ensure clean state
        window.location.reload();
      },
      onError: (error: any) => {
        message.error('Failed to reset configuration');
        // eslint-disable-next-line no-console
        console.error('Error resetting configuration:', error);
      },
    },
  );

  const resetToDefault = useCallback(
    (key: string) => {
      if (!key) {
        message.error('No key provided for persistence');
        return;
      }
      resetMutation.mutate(key);
    },
    [resetMutation],
  );

  return {
    resetToDefault,
    isResetting: resetMutation.isLoading,
  };
};
